import { createClient } from '@epilot/sdk/identity'
import type {
  Client,
  OrgEnablementCreate,
  OrgEnablementUpdate,
  PartnerClientCreate,
  PartnerClientUpdate,
  RotateSecretRequest
} from '@epilot/sdk/identity'

import { configureClient } from './api-utils'

import { config } from '@/configs/api.config'

let client: Client | undefined

const fallbackMessage = (status?: number) =>
  status === 409
    ? 'This record changed or the action is not allowed. Reload the record and review it before trying again.'
    : status === 403
    ? 'Managing Epilot IdP applications requires the admin or super-admin group in this stage’s admin portal.'
    : status === 401
    ? 'Your admin session expired. Sign in again.'
    : status === 400
    ? 'The API rejected this configuration. Check redirect URLs, scopes, approval details and any sector document.'
    : 'The Identity API request failed. Refresh to check the latest state before retrying.'

/**
 * identity-api answers every refusal with `{ status, error }` whose `error` is written for the
 * operator (which field is wrong, that a sector change needs `allow_sector_change`, that a policy
 * version is stale). Surface it, as the other modules do with their APIs' messages, and fall back to
 * a status-based text only when the body carries none. The Axios error itself is never propagated:
 * its request config holds the bearer and a secret response could be logged with it.
 */
const errorMessage = (error: unknown) => {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | null
  )?.response
  const body = response?.data as
    | { error?: unknown; message?: unknown }
    | undefined
  const detail = [body?.error, body?.message].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  )

  return detail
    ? `${detail.trim()}${
        response?.status === 409
          ? ' Reload the record before trying again.'
          : ''
      }`
    : fallbackMessage(response?.status)
}

const getIdentityClient = (): Client => {
  if (!config.IDENTITY)
    throw new Error('Identity API is not configured for this stage.')
  if (!client) {
    client = configureClient(createClient(), { baseURL: config.IDENTITY })
    client.defaults.timeout = 15000
    // Do not propagate Axios errors containing bearer headers or secret responses.
    client.interceptors.response.use(
      (response) => response,
      (error: unknown) => Promise.reject(new Error(errorMessage(error)))
    )
  }

  return client
}

export const identityClient = {
  list: async (cursor?: string) =>
    (await getIdentityClient().listClients({ size: 100, cursor })).data,
  get: async (id: string) =>
    (await getIdentityClient().getOidcClient({ client_id: id })).data,
  create: async (input: PartnerClientCreate) =>
    (await getIdentityClient().createOidcClient(null, input)).data,
  update: async (id: string, input: PartnerClientUpdate) =>
    (await getIdentityClient().updateClient({ client_id: id }, input)).data,
  activate: async (id: string) =>
    (await getIdentityClient().activateClient({ client_id: id })).data,
  suspend: async (id: string, reason: string) =>
    (await getIdentityClient().suspendClient({ client_id: id }, { reason }))
      .data,
  retire: async (id: string) =>
    (await getIdentityClient().retireClient({ client_id: id })).data,
  rotateSecret: async (id: string, input: RotateSecretRequest) =>
    (await getIdentityClient().rotateClientSecret({ client_id: id }, input))
      .data,
  organizations: async (id: string) =>
    // Explicit form: the generated type of this operation omits its path-level client_id.
    (
      await getIdentityClient().listClientOrganizations([
        { name: 'client_id', value: id, in: 'path' }
      ])
    ).data.results,
  enableOrganization: async (id: string, input: OrgEnablementCreate) =>
    (
      await getIdentityClient().enableClientOrganization(
        { client_id: id },
        input
      )
    ).data,
  updateOrganization: async (
    id: string,
    orgId: string,
    input: OrgEnablementUpdate
  ) =>
    (
      await getIdentityClient().updateClientOrganization(
        { client_id: id, org_id: orgId },
        input
      )
    ).data,
  activateOrganization: async (id: string, orgId: string) =>
    (
      await getIdentityClient().activateClientOrganization({
        client_id: id,
        org_id: orgId
      })
    ).data,
  suspendOrganization: async (id: string, orgId: string, reason: string) =>
    (
      await getIdentityClient().suspendClientOrganization(
        { client_id: id, org_id: orgId },
        { reason }
      )
    ).data,
  revokeOrganization: async (id: string, orgId: string, reason: string) =>
    (
      await getIdentityClient().revokeClientOrganization(
        { client_id: id, org_id: orgId },
        { reason }
      )
    ).data
}
